import { openRcdDialog, closeRcdDialog } from "./dialog";

const pickerSelector = "[data-rcd-customer-picker]";
const optionSelector = "[data-rcd-customer-option]";
const fieldSelector = (name) => `[data-rcd-customer-field="${name}"]`;

const debounce = (callback, delay = 180) => {
    let timer = 0;
    return (...args) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => callback(...args), delay);
    };
};

const buildOption = (customer) => {
    const option = document.createElement("button");
    option.type = "button";
    option.className = "rcd-search-select__option rcd-customer-picker__option";
    option.dataset.rcdSearchSelectOption = "";
    option.dataset.rcdCustomerOption = "";
    option.dataset.value = customer.id;
    option.dataset.label = customer.name;
    option.dataset.contactName = customer.contactName || "";
    option.dataset.contactEmail = customer.email || "";
    option.dataset.contactPhone = customer.phoneNumber || "";

    const identity = document.createElement("span");
    const name = document.createElement("strong");
    name.textContent = customer.name;
    identity.append(name);
    if (customer.contactName) {
        const contact = document.createElement("small");
        contact.textContent = customer.contactName;
        identity.append(contact);
    }
    option.append(identity);
    if (customer.email) {
        const email = document.createElement("small");
        email.textContent = customer.email;
        option.append(email);
    }
    return option;
};

const setPickerOption = (picker, customer) => {
    const optionsHost = picker.querySelector("[data-rcd-customer-options]");
    if (!optionsHost) return null;
    const existing = [...optionsHost.querySelectorAll(optionSelector)]
        .find((option) => option.dataset.value === customer.id);
    const option = buildOption(customer);
    if (existing) existing.replaceWith(option);
    else optionsHost.prepend(option);
    picker.dispatchEvent(new CustomEvent("rcd:search-select:refresh"));
    return option;
};

const syncAction = (picker, renderIcons) => {
    const action = picker.querySelector("[data-rcd-customer-action]");
    if (!action) return;
    const selected = Boolean(picker.querySelector("[data-rcd-customer-value]")?.value);
    const allowed = selected
        ? picker.dataset.rcdCustomerCanUpdate === "true"
        : picker.dataset.rcdCustomerCanCreate === "true";
    action.hidden = !allowed;
    action.setAttribute("aria-label", selected ? picker.dataset.rcdCustomerEditLabel : picker.dataset.rcdCustomerCreateLabel);
    action.innerHTML = `<i data-lucide="${selected ? "pencil" : "plus"}" aria-hidden="true"></i>`;
    renderIcons?.();
};

const updateCreateCopy = (picker) => {
    const button = picker.querySelector("[data-rcd-customer-create]");
    const input = picker.querySelector("[data-rcd-customer-input]");
    if (!button || !input) return;
    const query = input.value.trim();
    const template = picker.dataset.rcdCustomerAddTemplate || "Add ‘{0}’ as new customer";
    button.textContent = query ? template.replace("{0}", query) : picker.dataset.rcdCustomerCreateLabel;
    button.disabled = !query;
};

const replaceSearchResults = (picker, customers) => {
    const host = picker.querySelector("[data-rcd-customer-options]");
    if (!host) return;
    host.replaceChildren(...customers.map(buildOption));
    picker.dispatchEvent(new CustomEvent("rcd:search-select:refresh"));
    updateCreateCopy(picker);
};

const loadCustomers = async (picker, controller) => {
    const input = picker.querySelector("[data-rcd-customer-input]");
    if (!input) return;
    const url = new URL(picker.dataset.rcdCustomerSearchUrl || "/api/workspace/customers", window.location.origin);
    const query = input.value.trim();
    if (query) url.searchParams.set("search", query);
    picker.setAttribute("aria-busy", "true");
    try {
        const response = await fetch(url, { signal: controller.signal, headers: { "X-Requested-With": "XMLHttpRequest" } });
        if (!response.ok) return;
        replaceSearchResults(picker, await response.json());
    } catch (error) {
        if (error.name !== "AbortError") document.dispatchEvent(new CustomEvent("rcd:htmx-error", { detail: { status: 0, path: url.pathname } }));
    } finally {
        if (!controller.signal.aborted) picker.removeAttribute("aria-busy");
    }
};

const setupDialog = (renderIcons) => {
    const dialog = document.querySelector("[data-rcd-customer-dialog]");
    const form = dialog?.querySelector("[data-rcd-customer-form]");
    if (!dialog || !form || dialog.dataset.rcdCustomerDialogReady === "true") return;
    dialog.dataset.rcdCustomerDialogReady = "true";
    let activePicker = null;

    const field = (name) => form.querySelector(fieldSelector(name));
    const status = form.querySelector("[data-rcd-customer-status]");
    const title = form.querySelector("[data-rcd-customer-dialog-title]");
    const save = form.querySelector("[data-rcd-customer-save]");
    const sameName = form.querySelector("[data-rcd-customer-same-name]");
    const samePostal = form.querySelector("[data-rcd-customer-same-postal]");
    const postalSection = form.querySelector("[data-rcd-customer-postal-section]");
    const nameField = field("name");
    const contactField = field("contactName");
    const physicalAddressField = field("addressLine1");
    const postalAddressField = field("postalAddress");

    const setStatus = (message = "") => {
        if (!status) return;
        status.textContent = message;
        status.hidden = !message;
    };
    const setValue = (name, value) => {
        const target = field(name);
        if (target) target.value = value ?? "";
    };
    const fill = (customer = {}) => {
        setValue("id", customer.id);
        setValue("name", customer.name);
        setValue("contactName", customer.contactName);
        setValue("email", customer.email);
        setValue("phoneNumber", customer.phoneNumber);
        setValue("mobileNumber", customer.mobileNumber);
        setValue("addressLine1", customer.addressLine1);
        setValue("postalAddress", customer.postalAddress);
        setValue("addressLine2", customer.addressLine2);
        setValue("city", customer.city);
        setValue("region", customer.region);
        setValue("postalCode", customer.postalCode);
        setValue("country", customer.country);
        setValue("status", customer.status || "Active");
        const usesCustomerName = !customer.contactName || customer.name === customer.contactName;
        if (sameName) sameName.checked = usesCustomerName;
        if (contactField) {
            if (usesCustomerName) contactField.value = customer.name || "";
            contactField.disabled = usesCustomerName;
        }
        const usesPhysicalAddress = !customer.postalAddress || customer.postalAddress === customer.addressLine1;
        if (samePostal) samePostal.checked = usesPhysicalAddress;
        if (postalAddressField) {
            if (usesPhysicalAddress) postalAddressField.value = customer.addressLine1 || "";
            postalAddressField.disabled = usesPhysicalAddress;
        }
        if (postalSection) postalSection.hidden = usesPhysicalAddress;
    };
    const openEditor = (picker, customer = null) => {
        activePicker = picker;
        form.reset();
        fill(customer || { name: picker.querySelector("[data-rcd-customer-input]")?.value.trim() || "", status: "Active" });
        setStatus();
        const editing = Boolean(customer?.id);
        const customerName = field("name")?.value.trim();
        if (title) title.textContent = customerName || (editing ? picker.dataset.rcdCustomerEditLabel : picker.dataset.rcdCustomerCreateLabel);
        openRcdDialog(dialog, picker.querySelector("[data-rcd-customer-action], [data-rcd-customer-create]") || document.activeElement);
    };
    const openExisting = async (picker) => {
        const id = picker.querySelector("[data-rcd-customer-value]")?.value;
        if (!id) return openEditor(picker);
        const response = await fetch(`/api/workspace/customers/${encodeURIComponent(id)}`, { headers: { "X-Requested-With": "XMLHttpRequest" } });
        if (!response.ok) return;
        openEditor(picker, await response.json());
    };

    document.addEventListener("rcd:customer-picker:create", (event) => openEditor(event.detail.picker));
    document.addEventListener("rcd:customer-picker:edit", (event) => openExisting(event.detail.picker));
    sameName?.addEventListener("change", () => {
        if (contactField) {
            contactField.disabled = sameName.checked;
            if (sameName.checked) contactField.value = nameField?.value || "";
        }
    });
    nameField?.addEventListener("input", () => {
        if (sameName?.checked && contactField) contactField.value = nameField.value;
    });
    samePostal?.addEventListener("change", () => {
        if (postalAddressField) {
            postalAddressField.disabled = samePostal.checked;
            if (samePostal.checked) postalAddressField.value = physicalAddressField?.value || "";
        }
        if (postalSection) postalSection.hidden = samePostal.checked;
    });
    physicalAddressField?.addEventListener("input", () => {
        if (samePostal?.checked && postalAddressField) postalAddressField.value = physicalAddressField.value;
    });
    form.addEventListener("submit", async (event) => {
        event.preventDefault();
        if (!activePicker || !form.reportValidity()) return;
        const id = field("id")?.value || "";
        const payload = {
            id: id || null,
            name: field("name")?.value.trim() || "",
            contactName: field("contactName")?.value.trim() || null,
            email: field("email")?.value.trim() || null,
            phoneNumber: field("phoneNumber")?.value.trim() || null,
            mobileNumber: field("mobileNumber")?.value.trim() || null,
            addressLine1: field("addressLine1")?.value.trim() || null,
            postalAddress: field("postalAddress")?.value.trim() || null,
            addressLine2: field("addressLine2")?.value.trim() || null,
            city: field("city")?.value.trim() || null,
            region: field("region")?.value.trim() || null,
            postalCode: field("postalCode")?.value.trim() || null,
            country: field("country")?.value.trim() || null,
            status: field("status")?.value || "Active"
        };
        const token = form.querySelector('input[name="__RequestVerificationToken"]')?.value || "";
        if (save) save.disabled = true;
        setStatus();
        try {
            const response = await fetch(id ? `/api/workspace/customers/${encodeURIComponent(id)}` : "/api/workspace/customers", {
                method: id ? "PUT" : "POST",
                headers: { "Content-Type": "application/json", "RequestVerificationToken": token, "X-Requested-With": "XMLHttpRequest" },
                body: JSON.stringify(payload)
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) throw new Error(result.detail || result.message || dialog.dataset.rcdCustomerSaveFailed);
            const selectedId = activePicker.querySelector("[data-rcd-customer-value]")?.value || "";
            activePicker.dataset.rcdCustomerPreserveSites = String(Boolean(id && selectedId === result.id));
            const option = setPickerOption(activePicker, result);
            option?.click();
            closeRcdDialog(dialog, "saved");
        } catch (error) {
            setStatus(error.message);
        } finally {
            if (save) save.disabled = false;
            renderIcons?.();
        }
    });
};

const setupCustomerProfileForms = (root) => {
    root.querySelectorAll("[data-rcd-customer-profile-form]:not([data-rcd-customer-profile-ready])").forEach((form) => {
        form.dataset.rcdCustomerProfileReady = "true";
        const name = form.querySelector("[data-rcd-customer-profile-name]");
        const contact = form.querySelector("[data-rcd-customer-profile-contact]");
        const sameName = form.querySelector("[data-rcd-customer-profile-same-name]");
        const physical = form.querySelector("[data-rcd-customer-profile-physical]");
        const postal = form.querySelector("[data-rcd-customer-profile-postal]");
        const samePostal = form.querySelector("[data-rcd-customer-profile-same-postal]");
        const postalSection = form.querySelector("[data-rcd-customer-profile-postal-section]");

        const syncName = () => {
            if (!contact || !sameName) return;
            contact.readOnly = sameName.checked;
            if (sameName.checked) contact.value = name?.value || "";
        };
        const syncPostal = () => {
            if (!postal || !samePostal) return;
            postal.readOnly = samePostal.checked;
            if (samePostal.checked) postal.value = physical?.value || "";
            if (postalSection) postalSection.hidden = samePostal.checked;
        };

        sameName?.addEventListener("change", syncName);
        name?.addEventListener("input", syncName);
        samePostal?.addEventListener("change", syncPostal);
        physical?.addEventListener("input", syncPostal);
        form.addEventListener("submit", () => {
            syncName();
            syncPostal();
        });
        syncName();
        syncPostal();
    });
};

export const setupRcdCustomerPickers = (root = document, renderIcons) => {
    setupDialog(renderIcons);
    setupCustomerProfileForms(root);
    root.querySelectorAll(`${pickerSelector}:not([data-rcd-customer-picker-ready])`).forEach((picker) => {
        picker.dataset.rcdCustomerPickerReady = "true";
        const input = picker.querySelector("[data-rcd-customer-input]");
        const value = picker.querySelector("[data-rcd-customer-value]");
        const action = picker.querySelector("[data-rcd-customer-action]");
        let requestController = null;
        const validateSelection = () => {
            if (!input) return;
            const invalid = picker.dataset.rcdCustomerRequired === "true" && !value?.value;
            input.setCustomValidity(invalid ? picker.dataset.rcdCustomerRequiredMessage || "Select a customer." : "");
        };
        const syncDependentSites = () => {
            const form = picker.closest("form") || document;
            const customerId = value?.value || "";
            form.querySelectorAll("[data-rcd-customer-site-select]").forEach((siteSelect) => {
                siteSelect.querySelectorAll("option[data-customer-id]").forEach((option) => {
                    option.hidden = option.dataset.customerId !== customerId;
                });
                if (siteSelect.selectedOptions[0]?.hidden) siteSelect.value = "";
                siteSelect.dispatchEvent(new Event("change", { bubbles: true }));
            });
        };
        const requestSearch = debounce(() => {
            requestController?.abort();
            requestController = new AbortController();
            loadCustomers(picker, requestController);
        });

        input?.addEventListener("focus", requestSearch);
        input?.addEventListener("input", () => {
            updateCreateCopy(picker);
            window.requestAnimationFrame(() => syncAction(picker, renderIcons));
            requestSearch();
            window.requestAnimationFrame(validateSelection);
        });
        picker.addEventListener("rcd:search-select:change", (event) => {
            const option = event.detail?.option;
            const preserveDependentSelection = picker.dataset.rcdCustomerPreserveSites === "true";
            delete picker.dataset.rcdCustomerPreserveSites;
            syncAction(picker, renderIcons);
            validateSelection();
            if (!preserveDependentSelection) syncDependentSites();
            picker.dispatchEvent(new CustomEvent("rcd:customer-picker:change", {
                bubbles: true,
                detail: {
                    value: value?.value || "",
                    customer: option ? {
                        id: option.dataset.value,
                        name: option.dataset.label,
                        contactName: option.dataset.contactName || "",
                        email: option.dataset.contactEmail || "",
                        phoneNumber: option.dataset.contactPhone || ""
                    } : null,
                    option,
                    preserveDependentSelection
                }
            }));
        });
        picker.addEventListener("click", (event) => {
            const create = event.target.closest("[data-rcd-customer-create]");
            if (create) {
                event.preventDefault();
                document.dispatchEvent(new CustomEvent("rcd:customer-picker:create", { detail: { picker } }));
                return;
            }
            if (event.target.closest("[data-rcd-customer-action]")) {
                event.preventDefault();
                const type = value?.value ? "edit" : "create";
                document.dispatchEvent(new CustomEvent(`rcd:customer-picker:${type}`, { detail: { picker } }));
            }
        });
        updateCreateCopy(picker);
        syncAction(picker, renderIcons);
        syncDependentSites();
        validateSelection();
    });
};
